import React from 'react';
import {Card, MiniAreaChart} from '@/modules/ohd/components/primitives';
import type {ProgramReportingRow} from '../services/bioMatchReportingService';
import {wholeDollars} from '@/shared/utils/money';

/**
 * Real BioMatch program card for the OHD dashboard. Mirrors the
 * visual contract of `modules/ohd/components/ProgramCard` (the
 * "Data Pending" placeholder we're replacing) but bound to the
 * de-identified employer reporting payload.
 *
 * Engagement chart placeholder: time series isn't in v1 of the
 * reporting endpoint. The chart shows a flat zero line until daily
 * enrollment series ships — keeping the layout intact so the dashboard
 * doesn't reflow when the time-series upgrade lands.
 */

interface DailyPoint {
    day: string;
    enrollments: number;
}

interface Props {
    row: ProgramReportingRow;
    color: string;
    /**
     * Daily enrollment series for the past N days. Component fills in
     * zero-days so the chart line is continuous regardless of which
     * days had any enrollments.
     */
    dailySeries?: DailyPoint[];
    daysWindow?: number;
}

/** Build a continuous N-day series, padding zero-days. */
function buildSeries(series: DailyPoint[] | undefined, days: number): {day: string; value: number}[] {
    const byDay = new Map<string, number>();
    for (const p of series ?? []) byDay.set(p.day, p.enrollments);
    const out: {day: string; value: number}[] = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const key = d.toISOString().slice(0, 10);
        out.push({day: key.slice(5), value: byDay.get(key) ?? 0});
    }
    return out;
}

const BioMatchProgramCard: React.FC<Props> = ({row, color, dailySeries, daysWindow = 30}) => {
    const totalEnrollments = row.active_enrollments + row.completed_enrollments;
    const fulfillmentDone = row.shipped_count + row.lab_addon_complete;
    const showCostShare = row.patient_total_cents > 0;
    const chartData = buildSeries(dailySeries, daysWindow);

    return (
        <div>
            <div className="mb-2 flex items-center gap-2">
                <h3 className="text-[20px] text-mld-text-primary">{row.recommendation_name}</h3>
            </div>
            <Card>
                <div className="p-5">
                    <div className="mb-4 p-4">
                        <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-[14px] font-semibold text-mld-text-primary">Engagement</span>
                                {showCostShare && (
                                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-700">
                                        Cost Share Enabled
                                    </span>
                                )}
                            </div>
                            <span className="text-[12px] text-gray-400">Last {daysWindow} days</span>
                        </div>
                        <MiniAreaChart data={chartData} color={color} height={160} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <div>
                            <div className="text-[28px] leading-tight font-medium text-mld-text-primary">
                                {row.active_enrollments.toLocaleString('en-US')}
                            </div>
                            <div className="-mt-0.5 text-[12px] text-gray-500">Active enrollments</div>
                        </div>
                        <div>
                            <div className="text-[28px] leading-tight font-medium text-mld-text-primary">
                                {fulfillmentDone.toLocaleString('en-US')}
                            </div>
                            <div className="-mt-0.5 text-[12px] text-gray-500">Kits / lab add-ons fulfilled</div>
                        </div>
                        <div>
                            <div className="text-[28px] leading-tight font-medium text-mld-text-primary">
                                {row.lab_addon_total.toLocaleString('en-US')}
                            </div>
                            <div className="-mt-0.5 text-[12px] text-gray-500">Lab add-ons (existing draw)</div>
                        </div>
                        <div>
                            <div className="text-[28px] leading-tight font-medium text-mld-text-primary">
                                {wholeDollars(row.employer_total_cents)}
                            </div>
                            <div className="-mt-0.5 text-[12px] text-gray-500">Employer cost ({totalEnrollments} enrolled)</div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default BioMatchProgramCard;
